/**
 * Motor único de onboarding (Impulza · Gastify).
 *
 * Antes había tres motores en paralelo, cada uno con su propia máquina de
 * estado, su clave de localStorage y su (falta de) telemetría:
 *
 *   - recorrido de primera venta  → `impulza:first-sale-onboarding:v1` + tabla
 *     `onboarding_progress`, con pausa/reanudación y eventos de funnel;
 *   - tours de puesta a punto     → `impulza:setup-tour:v1`, solo localStorage,
 *     sin telemetría ni forma de retomar;
 *   - tours por módulo            → `organizations.module_onboarding_statuses`,
 *     por organización (no por usuario) y con telemetría a una tabla que no
 *     existe.
 *
 * Este archivo unifica las tres máquinas en una sola. NO define contenido: los
 * pasos, textos y selectores viven en cada app (Impulza y Gastify divergen ahí
 * a propósito) y se registran con `registerOnboardingTours`.
 *
 * Diseño deliberado: el motor no importa `../supabase` ni toca `window` de
 * forma directa. Todo efecto externo entra por `configureOnboardingEngine`, de
 * modo que sea testeable en node y que un fallo de red o de RLS nunca pueda
 * interrumpir el recorrido del usuario.
 */

export const ONBOARDING_STORAGE_KEY = 'impulza:onboarding:v2';
export const ONBOARDING_EVENT = 'impulza:onboarding-change';

// Claves de los dos motores anteriores. Se leen una única vez para migrar
// recorridos en curso y después se borran.
export const LEGACY_FIRST_SALE_KEY = 'impulza:first-sale-onboarding:v1';
export const LEGACY_SETUP_TOUR_KEY = 'impulza:setup-tour:v1';

// Un recorrido pausado más de N días es un abandono de hecho.
export const ONBOARDING_ABANDONED_AFTER_DAYS = 7;

const VALID_STATUSES = ['active', 'paused', 'completed'];

// ---------------------------------------------------------------------------
// Adaptadores inyectables
// ---------------------------------------------------------------------------

const isBrowser = () => typeof window !== 'undefined';

const defaultStorage = {
  getItem: (key) => (isBrowser() ? window.localStorage.getItem(key) : null),
  setItem: (key, value) => { if (isBrowser()) window.localStorage.setItem(key, value); },
  removeItem: (key) => { if (isBrowser()) window.localStorage.removeItem(key); },
};

const defaultEmit = (name, detail) => {
  if (isBrowser()) window.dispatchEvent(new CustomEvent(name, { detail }));
};

// La telemetría se carga en diferido y solo en el browser: importarla acá
// arrastraría el cliente de Supabase (y sus variables de entorno de Vite) a
// cualquier consumidor del motor, incluidos los tests.
const lazyTelemetry = (method) => (payload) => {
  if (!isBrowser()) return;
  import('./onboardingTelemetry.js')
    .then((mod) => mod?.[method]?.(payload))
    .catch(() => { /* la instrumentación nunca bloquea la operatoria */ });
};

const adapters = {
  storage: defaultStorage,
  emit: defaultEmit,
  track: lazyTelemetry('trackOnboardingEvent'),
  saveProgress: lazyTelemetry('persistOnboardingProgress'),
  loadProgress: async ({ userId, key }) => {
    if (!isBrowser()) return null;
    try {
      const mod = await import('./onboardingTelemetry.js');
      return await mod.loadOnboardingProgress({ userId, key });
    } catch {
      return null;
    }
  },
  now: () => Date.now(),
};

/**
 * Sustituye los adaptadores del motor. Pensado para tests y para entornos sin
 * DOM; en la app corren los defaults.
 */
export function configureOnboardingEngine(next = {}) {
  Object.assign(adapters, next);
}

// Ningún efecto externo puede tumbar el recorrido: si el tracker o el storage
// fallan, se sigue adelante.
const safely = (fn, fallback = null) => {
  try {
    return fn();
  } catch {
    return fallback;
  }
};

const track = (payload) => safely(() => adapters.track(payload));

/**
 * Registra un evento de onboarding por el mismo canal que el motor. Lo usan las
 * capas de contenido para sus hitos propios (por ejemplo la venta del recorrido
 * guiado) sin volver a importar la telemetría, de modo que exista un único
 * punto de salida — y un único punto que mockear en los tests.
 */
export function trackOnboardingEngineEvent(payload) {
  return track(payload);
}

// ---------------------------------------------------------------------------
// Registro de tours
// ---------------------------------------------------------------------------

const registry = new Map();

const normalizeStep = (step, index) => {
  if (!step || typeof step !== 'object') return null;
  return {
    ...step,
    id: step.id || `step-${index}`,
    selector: step.selector || null,
    placement: step.placement || 'bottom',
    title: step.title || '',
    description: step.description || '',
    detail: step.detail || null,
    route: step.route || null,
    options: Array.isArray(step.options) ? step.options : null,
  };
};

const normalizeTour = (tour) => {
  if (!tour || typeof tour !== 'object' || !tour.id) return null;

  const steps = (Array.isArray(tour.steps) ? tour.steps : [])
    .map(normalizeStep)
    .filter(Boolean);

  // Un tour sin pasos no se puede renderizar: se descarta en vez de dejar que
  // reviente al primer acceso a `steps[0]`.
  if (steps.length === 0) return null;

  return {
    ...tour,
    id: String(tour.id),
    kind: tour.kind || 'setup',
    title: tour.title || '',
    badge: tour.badge || null,
    description: tour.description || '',
    startRoute: tour.startRoute || null,
    persist: tour.persist === true,
    stepAliases: tour.stepAliases && typeof tour.stepAliases === 'object' ? tour.stepAliases : {},
    steps,
  };
};

/** Registra uno o varios tours. Acepta array u objeto indexado por id. */
export function registerOnboardingTours(tours) {
  const list = Array.isArray(tours) ? tours : Object.values(tours || {});
  list.forEach((tour) => {
    const normalized = normalizeTour(tour);
    if (normalized) registry.set(normalized.id, normalized);
  });
  return registry.size;
}

export function getOnboardingTour(tourId) {
  if (!tourId) return null;
  return registry.get(String(tourId)) || null;
}

export function listOnboardingTours(kind = null) {
  const all = [...registry.values()];
  return kind ? all.filter((tour) => tour.kind === kind) : all;
}

/** Solo para tests: vacía el registro. */
export function clearOnboardingTours() {
  registry.clear();
}

// ---------------------------------------------------------------------------
// Resolución de pasos
// ---------------------------------------------------------------------------

const clampStepIndex = (tour, index) => {
  const parsed = Number.isFinite(Number(index)) ? Math.trunc(Number(index)) : 0;
  return Math.max(0, Math.min(parsed, tour.steps.length - 1));
};

/**
 * Traduce un id de paso a su índice. Contempla los alias declarados por el
 * tour: las páginas que viajan por sync emiten los ids del recorrido de la otra
 * app, y sin esta traducción ese avance sería un no-op silencioso.
 */
export function resolveOnboardingStepIndex(tour, stepId) {
  if (!tour || !stepId) return -1;
  const target = tour.stepAliases[stepId] || stepId;
  return tour.steps.findIndex((step) => step.id === target);
}

/** Id estable del paso, para telemetría legible: `tourId:stepId`. */
const stepEventId = (tour, stepIndex) => {
  const step = tour?.steps?.[stepIndex];
  if (!step) return null;
  // El recorrido de demo tiene ids propios y legibles; los tours de puesta a
  // punto usan índices, así que se los califica con el id del tour.
  return step.id.startsWith('step-') ? `${tour.id}:${stepIndex}` : step.id;
};

// ---------------------------------------------------------------------------
// Estado
// ---------------------------------------------------------------------------

const normalizeState = (value) => {
  if (!value || typeof value !== 'object') return null;

  const tour = getOnboardingTour(value.tourId);
  // Un estado que apunta a un tour desregistrado (contenido renombrado o
  // eliminado en un deploy) se descarta: es preferible perder el recorrido a
  // dejar al usuario con un cartel sin destino.
  if (!tour) return null;

  return {
    version: 2,
    tourId: tour.id,
    stepIndex: clampStepIndex(tour, value.stepIndex),
    status: VALID_STATUSES.includes(value.status) ? value.status : 'active',
    organizationId: value.organizationId || null,
    userId: value.userId || null,
    businessType: value.businessType || null,
    context: value.context && typeof value.context === 'object' ? value.context : {},
    startedAt: value.startedAt || null,
    updatedAt: value.updatedAt || null,
    abandonedLoggedAt: value.abandonedLoggedAt || null,
    // Recorrido que quedó suspendido para dar paso a este. Un solo nivel: no
    // se apilan recorridos indefinidamente (ver `startOnboardingTour`).
    previous: value.previous ? { ...value.previous, previous: null } : null,
  };
};

/**
 * Restaura el recorrido que estaba en curso antes de arrancar el actual. Se usa
 * al terminar o cancelar un tour anidado: el usuario vuelve solo a donde estaba.
 */
const restorePrevious = (state) => {
  const previous = state?.previous;
  if (!previous) return writeOnboardingState(null);
  // Se restaura conservando su status. Si estaba pausado no reaparece de
  // sorpresa (un recorrido pausado no se renderiza), pero tampoco se pierde:
  // el dashboard lo sigue ofreciendo con "Retomar el recorrido guiado".
  return writeOnboardingState({ ...previous, previous: null });
};

export function readOnboardingState() {
  const raw = safely(() => adapters.storage.getItem(ONBOARDING_STORAGE_KEY));
  if (!raw) return null;
  const parsed = safely(() => JSON.parse(raw));
  return normalizeState(parsed);
}

export function writeOnboardingState(state) {
  const normalized = normalizeState(state);

  if (!normalized) {
    safely(() => adapters.storage.removeItem(ONBOARDING_STORAGE_KEY));
    safely(() => adapters.emit(ONBOARDING_EVENT, null));
    return null;
  }

  // `updatedAt` marca la última actividad real del usuario, no la última
  // escritura: hidratar desde el servidor o registrar un abandono no puede
  // rejuvenecer un recorrido viejo (si lo hiciera, `maybeTrackOnboardingAbandoned`
  // no detectaría nunca un abandono al volver desde otro dispositivo).
  const stamped = {
    ...normalized,
    updatedAt: normalized.updatedAt || new Date(adapters.now()).toISOString(),
  };
  safely(() => adapters.storage.setItem(ONBOARDING_STORAGE_KEY, JSON.stringify(stamped)));
  safely(() => adapters.emit(ONBOARDING_EVENT, stamped));

  const tour = getOnboardingTour(stamped.tourId);
  if (tour?.persist && stamped.userId) {
    safely(() => adapters.saveProgress({
      userId: stamped.userId,
      organizationId: stamped.organizationId,
      key: tour.id,
      state: stamped,
    }));
  }

  return stamped;
}

/**
 * Marca el estado como actividad del usuario. Las transiciones del recorrido
 * lo usan; hidratar o registrar un abandono NO, para no falsear la antigüedad.
 */
const touched = (state) => ({ ...state, updatedAt: new Date(adapters.now()).toISOString() });

const patchState = (patch = {}) => {
  const current = readOnboardingState();
  if (!current) return null;
  return writeOnboardingState(touched({ ...current, ...patch }));
};

// ---------------------------------------------------------------------------
// Ciclo de vida
// ---------------------------------------------------------------------------

export function startOnboardingTour({
  tourId,
  organizationId = null,
  userId = null,
  businessType = null,
  context = {},
  stepIndex = 0,
  fresh = false,
} = {}) {
  const tour = getOnboardingTour(tourId);
  if (!tour) return null;

  // Con un único estado compartido, arrancar una guía en medio de otra la
  // pisaría. El recorrido en curso se suspende y se retoma al terminar la
  // guía nueva: es el caso del último tramo de la demo, que invita a lanzar
  // las guías de puesta a punto.
  //
  // `fresh` es lo contrario: reiniciar el recorrido principal es empezar de
  // cero. Sin esto, la guía que estaba abierta quedaba guardada y volvía a
  // escena al terminar la demo, en vez de dejar al usuario donde el recorrido
  // lo llevó.
  const current = fresh ? null : readOnboardingState();
  const previous = current && current.tourId !== tour.id
    ? { ...current, previous: null }
    : current?.previous || null;

  const startedAt = new Date(adapters.now()).toISOString();
  const state = writeOnboardingState({
    tourId: tour.id,
    previous,
    stepIndex,
    status: 'active',
    organizationId,
    userId,
    businessType,
    context,
    startedAt,
    updatedAt: startedAt,
  });

  if (state) {
    track({
      event: 'tour_started',
      stepId: stepEventId(tour, state.stepIndex),
      organizationId: state.organizationId,
      userId: state.userId,
      metadata: { tourId: tour.id, kind: tour.kind, businessType: state.businessType },
    });
  }

  return state;
}

export function advanceOnboardingTour() {
  const current = readOnboardingState();
  if (!current) return null;

  const tour = getOnboardingTour(current.tourId);
  if (!tour) return null;

  // El evento reporta el paso que el usuario terminó, no el siguiente: así el
  // funnel se lee como "hasta dónde llegó".
  track({
    event: 'tour_step_completed',
    stepId: stepEventId(tour, current.stepIndex),
    organizationId: current.organizationId,
    userId: current.userId,
    metadata: { tourId: tour.id, kind: tour.kind, stepIndex: current.stepIndex },
  });

  if (current.stepIndex + 1 >= tour.steps.length) return completeOnboardingTour();

  return writeOnboardingState(touched({ ...current, stepIndex: current.stepIndex + 1 }));
}

export function prevOnboardingTour() {
  const current = readOnboardingState();
  if (!current) return null;
  return writeOnboardingState(touched({ ...current, stepIndex: Math.max(0, current.stepIndex - 1) }));
}

export function goToOnboardingStep(stepIndex) {
  const current = readOnboardingState();
  if (!current) return null;
  const tour = getOnboardingTour(current.tourId);
  if (!tour) return null;
  return writeOnboardingState(touched({ ...current, stepIndex: clampStepIndex(tour, stepIndex) }));
}

export function goToOnboardingStepById(stepId) {
  const current = readOnboardingState();
  if (!current) return null;
  const tour = getOnboardingTour(current.tourId);
  const index = resolveOnboardingStepIndex(tour, stepId);
  // Un id desconocido no mueve el recorrido: mejor quedarse donde está que
  // saltar a un paso arbitrario.
  if (index < 0) return null;
  return writeOnboardingState(touched({ ...current, stepIndex: index }));
}

export function completeOnboardingTour() {
  const current = readOnboardingState();
  if (!current) return null;
  const tour = getOnboardingTour(current.tourId);

  const next = writeOnboardingState(touched({ ...current, status: 'completed' }));
  track({
    event: 'tour_completed',
    stepId: stepEventId(tour, current.stepIndex),
    organizationId: current.organizationId,
    userId: current.userId,
    metadata: { tourId: current.tourId, kind: tour?.kind },
  });

  // Si este tour se había abierto sobre otro, el de abajo vuelve a escena.
  if (current.previous) return restorePrevious(current);

  return next;
}

/** Sale del recorrido conservándolo: el dashboard puede ofrecer "Retomar". */
export function pauseOnboardingTour() {
  const current = readOnboardingState();
  if (!current) return null;
  const tour = getOnboardingTour(current.tourId);

  const next = writeOnboardingState(touched({ ...current, status: 'paused' }));
  track({
    event: 'tour_paused',
    stepId: stepEventId(tour, current.stepIndex),
    organizationId: current.organizationId,
    userId: current.userId,
    metadata: { tourId: current.tourId, kind: tour?.kind, stepIndex: current.stepIndex },
  });
  return next;
}

export function resumeOnboardingTour() {
  return patchState({ status: 'active' });
}

export function cancelOnboardingTour() {
  const current = readOnboardingState();
  if (current?.previous) return restorePrevious(current);
  return writeOnboardingState(null);
}

/** Guarda datos del recorrido (producto creado, venta simulada) sin mover el paso. */
export function patchOnboardingContext(patch = {}) {
  const current = readOnboardingState();
  if (!current) return null;
  return writeOnboardingState(touched({ ...current, context: { ...current.context, ...patch } }));
}

// ---------------------------------------------------------------------------
// Identidad, pausa y abandono
// ---------------------------------------------------------------------------

export function matchesOnboardingIdentity(state, { organizationId, userId } = {}) {
  if (!state || state.status !== 'active') return false;
  if (organizationId && state.organizationId && state.organizationId !== organizationId) return false;
  if (userId && state.userId && state.userId !== userId) return false;
  return true;
}

export function readPausedOnboardingTour({ organizationId, userId } = {}) {
  const state = readOnboardingState();
  if (!state || state.status !== 'paused') return null;
  if (organizationId && state.organizationId && state.organizationId !== organizationId) return null;
  if (userId && state.userId && state.userId !== userId) return null;
  return state;
}

/**
 * Emite `tour_abandoned` una sola vez cuando un recorrido lleva demasiado
 * tiempo pausado. No cambia el status: sigue siendo retomable, solo queda
 * registrado para el funnel y para futuros recordatorios.
 */
export function maybeTrackOnboardingAbandoned(state) {
  if (!state || state.status !== 'paused' || state.abandonedLoggedAt) return state;

  const lastTouch = Date.parse(state.updatedAt || state.startedAt || '') || 0;
  if (!lastTouch) return state;
  if (adapters.now() - lastTouch < ONBOARDING_ABANDONED_AFTER_DAYS * 86400000) return state;

  track({
    event: 'tour_abandoned',
    stepId: stepEventId(getOnboardingTour(state.tourId), state.stepIndex),
    organizationId: state.organizationId,
    userId: state.userId,
    metadata: { tourId: state.tourId, kind: getOnboardingTour(state.tourId)?.kind, stepIndex: state.stepIndex },
  });

  return writeOnboardingState({ ...state, abandonedLoggedAt: new Date(adapters.now()).toISOString() });
}

// ---------------------------------------------------------------------------
// Persistencia entre dispositivos
// ---------------------------------------------------------------------------

export async function hydrateOnboardingTour({ organizationId, userId, tourId } = {}) {
  if (!userId || !tourId) return null;

  const local = readOnboardingState();
  // Lo local manda: el usuario está operando en este dispositivo.
  if (matchesOnboardingIdentity(local, { organizationId, userId })) return local;

  const remote = await safely(
    () => adapters.loadProgress({ userId, key: tourId }),
    Promise.resolve(null),
  );
  const serverState = normalizeState(await remote);
  if (!serverState) return maybeTrackOnboardingAbandoned(local);
  if (organizationId && serverState.organizationId && serverState.organizationId !== organizationId) return local;
  if (userId && serverState.userId && serverState.userId !== userId) return local;
  if (serverState.status === 'completed') return local;

  // Releer antes de escribir: la consulta al servidor tarda, y en el medio el
  // usuario pudo arrancar un recorrido en este dispositivo (por ejemplo,
  // reiniciar el tutorial, que además navega y vuelve a disparar la
  // hidratación). Sin esto, la respuesta del servidor llegaba tarde y pisaba
  // el recorrido recién iniciado con el progreso viejo.
  const localNow = readOnboardingState();
  if (matchesOnboardingIdentity(localNow, { organizationId, userId })) return localNow;
  if (localNow) {
    const localTime = Date.parse(localNow.updatedAt || localNow.startedAt || '') || 0;
    const remoteTime = Date.parse(serverState.updatedAt || serverState.startedAt || '') || 0;
    if (localTime > remoteTime) return localNow;
  }

  return maybeTrackOnboardingAbandoned(writeOnboardingState(serverState));
}

// ---------------------------------------------------------------------------
// Rutas
// ---------------------------------------------------------------------------

export function getOnboardingRoute(state) {
  if (!state) return null;
  const tour = getOnboardingTour(state.tourId);
  if (!tour) return null;
  return tour.steps[state.stepIndex]?.route || tour.startRoute || null;
}

const baseRoute = (route) => String(route).split('?')[0];
const routeMatches = (pathname, route) => {
  const base = baseRoute(route);
  return pathname === base || pathname.startsWith(`${base}/`);
};

/**
 * Un tour solo tiene sentido en la pantalla donde viven sus selectores. Si el
 * usuario navega a otra ruta, el cartel se seguiría renderizando sin poder
 * resaltar nada (y tapando otros recorridos).
 */
export function tourMatchesRoute(tour, pathname, stepIndex = 0) {
  if (!tour) return false;
  if (!pathname) return true;

  const step = tour.steps[clampStepIndex(tour, stepIndex)];

  // Paso sin selector (bienvenida, cierre): vale en la ruta de inicio del tour o rutas de sus pasos.
  if (step && !step.selector) {
    const route = step.route || tour.startRoute;
    if (route && routeMatches(pathname, route)) return true;
  }

  const currentStepRoutes = [
    step?.route,
    tour.startRoute,
    tour.finishRoute,
    ...(step?.options || []).map((option) => option.route),
  ].filter(Boolean);

  if (currentStepRoutes.some((route) => routeMatches(pathname, route))) return true;

  // Permitir todas las rutas declaradas en cualquiera de los pasos del tour
  // para que navegar entre pasos (ej: /sistema/proveedores -> /sistema/proveedores-nuevo)
  // mantenga activo el tour y no desmonte el asistente.
  const allTourRoutes = (tour.steps || [])
    .map((s) => s.route)
    .filter(Boolean);

  if (allTourRoutes.some((route) => routeMatches(pathname, route))) return true;

  // Tour sin rutas declaradas: no se esconde (no romper comportamiento previo).
  if (currentStepRoutes.length === 0 && allTourRoutes.length === 0) return true;

  return false;
}

// ---------------------------------------------------------------------------
// Lectura para la UI
// ---------------------------------------------------------------------------

export function getOnboardingProgress(state) {
  const tour = getOnboardingTour(state?.tourId);
  if (!tour) return { stepIndex: 0, humanStep: 0, total: 0, pct: 0 };

  const stepIndex = clampStepIndex(tour, state.stepIndex);
  const total = tour.steps.length;
  return {
    stepIndex,
    humanStep: stepIndex + 1,
    total,
    pct: Math.round((stepIndex / total) * 100),
  };
}

/** Paso activo listo para renderizar, con su tour y su progreso. */
export function buildOnboardingStepDefinition(state) {
  const tour = getOnboardingTour(state?.tourId);
  if (!tour) return null;

  const progress = getOnboardingProgress(state);
  return {
    tourId: tour.id,
    kind: tour.kind,
    title: tour.title,
    badge: tour.badge,
    description: tour.description,
    step: tour.steps[progress.stepIndex],
    steps: tour.steps,
    progress,
    isLast: progress.stepIndex === tour.steps.length - 1,
    context: state.context || {},
  };
}

// ---------------------------------------------------------------------------
// Compatibilidad con los motores anteriores
// ---------------------------------------------------------------------------

const readLegacy = (key) => {
  const raw = safely(() => adapters.storage.getItem(key));
  if (!raw) return null;
  return safely(() => JSON.parse(raw));
};

/**
 * Migra un recorrido en curso de cualquiera de los dos motores anteriores.
 * Se ejecuta al arrancar la app: el usuario que estaba a mitad de camino sigue
 * donde estaba en vez de perder el progreso en el deploy.
 *
 * @param {object} options
 * @param {string} options.demoTourId  id del tour que reemplaza al recorrido de
 *   primera venta en esta app (los stepId legacy se resuelven contra él).
 */
export function migrateLegacyOnboardingState({ demoTourId = 'first-sale-demo' } = {}) {
  const legacyFirstSale = readLegacy(LEGACY_FIRST_SALE_KEY);
  const legacySetupTour = readLegacy(LEGACY_SETUP_TOUR_KEY);

  // Las claves viejas se limpian aunque no se migre nada: evita reintentar la
  // migración en cada arranque y deja un solo origen de verdad.
  safely(() => adapters.storage.removeItem(LEGACY_FIRST_SALE_KEY));
  safely(() => adapters.storage.removeItem(LEGACY_SETUP_TOUR_KEY));

  // Ya hay un recorrido con el motor nuevo: no se pisa.
  if (readOnboardingState()) return null;

  if (legacyFirstSale?.stepId) {
    const tour = getOnboardingTour(demoTourId);
    const index = resolveOnboardingStepIndex(tour, legacyFirstSale.stepId);
    if (tour && index >= 0) {
      const { product = null, sale = null } = legacyFirstSale;
      return writeOnboardingState({
        tourId: tour.id,
        stepIndex: index,
        status: legacyFirstSale.status,
        organizationId: legacyFirstSale.organizationId,
        userId: legacyFirstSale.userId,
        businessType: legacyFirstSale.businessType,
        context: { product, sale },
        startedAt: legacyFirstSale.startedAt,
        abandonedLoggedAt: legacyFirstSale.abandonedLoggedAt,
      });
    }
  }

  if (legacySetupTour?.stepId) {
    const tour = getOnboardingTour(legacySetupTour.stepId);
    if (tour) {
      return writeOnboardingState({
        tourId: tour.id,
        stepIndex: legacySetupTour.stepIndex,
        status: legacySetupTour.active === false ? 'paused' : 'active',
      });
    }
  }

  return null;
}
